"use client";

import { useState } from "react";
import { MapPin, Copy, RefreshCw } from "lucide-react";
import GeneratorPage from "../GeneratorPage";
import { CATEGORIES } from "@/constants/categories";
import { Button, Input, Select } from "@/components/design-system";
import { ADDRESS_GENERATORS, AVAILABLE_COUNTRIES } from "@/lib/addressData";
import { useTranslations, useLocale } from "next-intl";
import { Controller } from "react-hook-form";
import SchemaScript from "@/components/common/SchemaScript";
import { Routes } from "@/constants/routes";
import { Locale } from "@/constants/locales";

interface GeneratedAddress {
    id: string;
    fullAddress: string;
    street: string;
    city: string; // or city/state
    zip: string | number;
    country: string;
}

export default function FakeAddressGenerator() {
    const t = useTranslations("addresses");
    const locale = useLocale() as Locale;
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<GeneratedAddress[]>([]);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Default form state
    const [fallbackForm] = useState({
        country: "US",
        count: 5,
    });

    const handleGenerate = (e: React.FormEvent, formData?: any) => {
        e.preventDefault();
        const data = formData || fallbackForm;
        const countryCode = data.country as keyof typeof ADDRESS_GENERATORS;
        const count = Number(data.count);

        setIsGenerating(true);

        // Find the generator for the selected country, default to US if not found
        const generator = ADDRESS_GENERATORS[countryCode] || ADDRESS_GENERATORS["US"];

        setTimeout(() => {
            const results: GeneratedAddress[] = [];
            for (let i = 0; i < count; i++) {
                const addr = generator.generate();
                results.push({
                    id: Math.random().toString(36).substr(2, 9),
                    ...addr
                });
            }
            setGenerated(results);
            setIsGenerating(false);
        }, 400);
    };

    const handleCopy = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1500);
    };

    const handleCopyAll = () => {
        const allText = generated.map(g => g.fullAddress).join("\n\n");
        navigator.clipboard.writeText(allText);
        setCopiedId("all");
        setTimeout(() => setCopiedId(null), 1500);
    };

    // Custom results renderer
    const customResultsRenderer = (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                {generated.length > 0 && (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={handleCopyAll}
                    >
                        {copiedId === "all" ? t("results.copyAllSuccess") : t("results.copyAll")}
                    </Button>
                )}
            </div>
            <div className="grid gap-4 md:grid-cols-1">
                {generated.length === 0 ? (
                    <div className="bg-muted/30 border border-dashed border-muted-foreground/25 rounded-lg p-8 text-center min-h-[120px] flex flex-col items-center justify-center">
                        <MapPin className="h-8 w-8 text-muted-foreground/50 mb-3" />
                        <span className="text-muted-foreground">{t("results.noResults")}</span>
                    </div>
                ) : (
                    generated.map((addr) => (
                        <div key={addr.id} className="relative bg-white dark:bg-slate-900 rounded-xl border border-border p-5 shadow-sm hover:shadow-md transition-all group">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={() => handleCopy(addr.fullAddress, addr.id)}
                                    className="p-2 text-muted-foreground hover:text-primary bg-background border rounded-md shadow-sm"
                                    title={t("form.copyButton")}
                                >
                                    {copiedId === addr.id ? <span className="text-green-500 font-bold text-xs">{t("form.copiedButton")}</span> : <Copy className="h-4 w-4" />}
                                </button>
                            </div>

                            <div className="flex items-start gap-4">
                                <div className="mt-1 p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg text-emerald-600 dark:text-emerald-400">
                                    <MapPin className="h-5 w-5" />
                                </div>
                                <div className="space-y-1">
                                    <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{addr.country}</div>
                                    <div className="font-mono text-lg text-foreground whitespace-pre-line">{addr.fullAddress}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    // Form config
    const formConfig = {
        values: fallbackForm,
        fields: [
            {
                key: "country",
                label: t("form.country"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                validation: { required: true, min: 1, max: 20 },
            },
        ],
        onChange: () => { },
    };

    return (
        <>
            <GeneratorPage
                title={t("title")}
                description={t("description")}
                icon={<MapPin className="h-4 w-4" />}
                category={CATEGORIES.personal}
                generatedData={generated.length ? generated.map(g => g.fullAddress).join("\n") : null}
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                formConfig={formConfig}
                resultsRenderer={customResultsRenderer}
                faqs={t.raw("faqs")}
                similarGenerators={[]}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">{t("form.country")}</label>
                        <Controller
                            name="country"
                            render={({ field }) => (
                                <Select
                                    value={field.value}
                                    onChange={(e) => field.onChange(e.target.value)}
                                >
                                    {AVAILABLE_COUNTRIES.map(c => (
                                        <option key={c.code} value={c.code}>{c.name}</option>
                                    ))}
                                </Select>
                            )}
                        />
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.count")}</label>
                        <Controller
                            name="count"
                            rules={{ required: true, min: 1, max: 20 }}
                            render={({ field }) => (
                                <Input
                                    type="number"
                                    min={1}
                                    max={20}
                                    value={field.value}
                                    onChange={(e) => field.onChange(Number(e.target.value))}
                                    placeholder={t("form.countPlaceholder")}
                                />
                            )}
                        />
                    </div>
                </div>
            </GeneratorPage>

            <SchemaScript route={Routes.addresses} locale={locale} faqItems={t.raw("faqs")} />
        </>
    );
}
